import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useApi } from '@/lib/ConfigProvider'
import { Icon } from '@/components/Icon'
import {
  fetchBeneficiaries,
  removeBeneficiary,
  renameBeneficiary,
  saveBeneficiary,
} from '@/payees/beneficiaries'
import {
  bareTag,
  checkTag,
  claimTag,
  fetchMyTag,
  lookupTag,
  saveByTag,
  unavailableBecause,
} from '@/tags/tags'
import type { Beneficiary, BeneficiariesState } from '@/payees/beneficiaries'
import type { AvailabilityState, MyTagState, TagLookupState } from '@/tags/tags'
import './People.css'

/**
 * People — the payees you keep who bank elsewhere (go-wallet#1064).
 *
 * # What was here before
 *
 * `<Placeholder title="People" />`. The rail named a destination and the screen
 * said, honestly, that it was not built. It could not have been: the backend
 * had no storage for a payee outside Tesora, so the only way to pay the same
 * supplier twice was to retype their account number and their name both times.
 *
 * # The address is not editable, and the form says so rather than hiding it
 *
 * Only the label can be changed. An account number that changed under a
 * familiar name is the shape of every invoice-fraud story there is, so moving
 * the money means adding the new payee and removing the old one — two
 * deliberate acts, both visible in the list. A screen that offered an edit
 * field and refused the save would be worse than one that never offers it.
 *
 * # A full account number is on this screen, and that is the point of it
 *
 * The list shows `masked` at a glance and the full IBAN under each entry,
 * because the whole reason to keep a payee is not to type it again. The backend
 * seals it at rest under a key destroyed when a customer is erased; that is a
 * property of its table, not something this screen has to compensate for.
 *
 * # 404 is not an empty list
 *
 * A deployment without the sealing key mounts none of these routes, so the read
 * fails and this renders `unavailable` — never "you have not added anyone yet",
 * which would tell somebody their address book is empty when the truth is that
 * we cannot keep one here.
 */
export function People() {
  const api = useApi()
  const [state, setState] = useState<BeneficiariesState>({ status: 'loading' })

  const load = useCallback(async () => {
    setState(await fetchBeneficiaries(api))
  }, [api])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <section className="payees">
      <header className="payees__head">
        <h1 className="payees__title">People</h1>
        <p className="payees__lede">
          Everybody you pay. Somebody on Tesora can be found by their tag; anybody else by their
          account number. Nothing here moves money on its own.
        </p>
      </header>

      <FindByTag />

      <AddPayee onSaved={load} />

      <section className="payees__list" aria-label="Saved payees">
        {state.status === 'ready' && state.beneficiaries.length > 0 ? (
          <ul className="payees__items">
            {state.beneficiaries.map((b) => (
              <PayeeRow key={b.reference} payee={b} onChanged={load} />
            ))}
          </ul>
        ) : (
          <p className="payees__empty">
            {state.status === 'loading'
              ? 'Reading your payees…'
              : state.status === 'ready'
                ? 'You have not saved anyone yet. Add somebody above and they will be here next time.'
                : // NOT "you have saved nobody" (go-wallet-web#22, #140). A
                  // deployment without the sealing key mounts no routes at all,
                  // so this is the ordinary answer there — and telling somebody
                  // their address book is empty when we simply cannot keep one
                  // sends them to support about the wrong thing.
                  `We could not read your payees. ${state.reason}`}
          </p>
        )}
      </section>
    </section>
  )
}

/** One saved payee, with the two things you may do to it. */
function PayeeRow({ payee, onChanged }: { payee: Beneficiary; onChanged: () => Promise<void> }) {
  const api = useApi()
  const [renaming, setRenaming] = useState(false)
  const [label, setLabel] = useState(payee.label)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()

  const rename = async () => {
    setBusy(true)
    setError(undefined)
    const result = await renameBeneficiary(api, payee.reference, label)
    setBusy(false)
    if (typeof result === 'string') {
      setError(result)
      return
    }
    setRenaming(false)
    await onChanged()
  }

  const remove = async () => {
    setBusy(true)
    setError(undefined)
    const failed = await removeBeneficiary(api, payee.reference)
    setBusy(false)
    if (failed !== undefined) {
      setError(failed)
      return
    }
    await onChanged()
  }

  return (
    <li className="payee">
      <div className="payee__who">
        {renaming ? (
          <div className="payee__renameRow">
            <input
              className="payee__input"
              aria-label="Name for this payee"
              value={label}
              maxLength={60}
              onChange={(e) => setLabel(e.target.value)}
            />
            <button type="button" className="payee__save" disabled={busy} onClick={() => void rename()}>
              Save
            </button>
            <button
              type="button"
              className="payee__cancel"
              disabled={busy}
              onClick={() => {
                setLabel(payee.label)
                setError(undefined)
                setRenaming(false)
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <span className="payee__label">{payee.label}</span>
            {/*
              THE NAME THEIR BANK HOLDS, always rendered beside the label. A
              screen showing only a nickname lets somebody instruct a payment
              without ever seeing the name it will carry — and the two are
              deliberately separate fields for exactly that reason.
            */}
            <span className="payee__name">{payee.creditorName}</span>
          </>
        )}
      </div>

      <div className="payee__where">
        <span className="payee__masked tnum">{payee.masked}</span>
        {/* The full number, because not retyping it is the whole point. */}
        <span className="payee__iban tnum">{payee.creditorIBAN}</span>
        {payee.creditorBIC !== undefined ? (
          <span className="payee__bic tnum">{payee.creditorBIC}</span>
        ) : null}
      </div>

      <div className="payee__actions">
        {!renaming ? (
          <button type="button" className="payee__action" onClick={() => setRenaming(true)}>
            Rename
          </button>
        ) : null}
        <button
          type="button"
          className="payee__action payee__action--remove"
          disabled={busy}
          onClick={() => void remove()}
        >
          Remove
        </button>
      </div>

      {error !== undefined ? <p className="payee__error">{error}</p> : null}
    </li>
  )
}

/**
 * Choose your own tag, when you have not got one.
 *
 * # It is here rather than in Settings
 *
 * This is the screen where a tag is useful and it is the screen where you find
 * out you have not got one. Sending somebody to another page to claim it — which
 * is what the copy did before this existed, to a Settings screen that had no
 * such control — is a dead end dressed as an instruction.
 *
 * # A claim can be HELD, and that is not a failure
 *
 * A name that needs an impersonation check comes back `pending_review`: it is
 * yours, nobody else can take it, and it does not resolve yet. Rendering it as
 * live would put a working-looking payment address in front of somebody whose
 * payers would reach nobody.
 *
 * # The backend's refusal is rendered verbatim
 *
 * Taken, reserved, already yours, a cooldown that has not run — four different
 * things a person can act on differently, and the backend writes the sentence
 * for each. A friendlier one invented here would be less true.
 */
function ClaimTag({ onClaimed }: { onClaimed: (m: MyTagState) => void }) {
  const api = useApi()
  const ids = useId()
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const [free, setFree] = useState<AvailabilityState>({ status: 'idle' })

  /**
   * Ask whether the name is free, debounced.
   *
   * Debounced for the fee quote's reason: a check per keystroke is a request per
   * keystroke while somebody is still typing the name it would check. Last write
   * wins by GENERATION rather than by arrival — a slow answer about an earlier
   * name must never overwrite a fresh one, or somebody reads "free" about a name
   * they have already changed.
   */
  const generation = useRef(0)
  useEffect(() => {
    const tag = bareTag(typed)
    if (tag === '') {
      setFree({ status: 'idle' })
      return
    }
    const mine = ++generation.current
    setFree({ status: 'checking' })
    const t = setTimeout(() => {
      void checkTag(api, tag).then((a) => {
        if (mine === generation.current) setFree(a)
      })
    }, 300)
    return () => clearTimeout(t)
  }, [api, typed])

  const claim = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(undefined)
    const result = await claimTag(api, typed)
    setBusy(false)
    if (!result.ok) {
      setError(result.reason)
      return
    }
    onClaimed(
      result.pending
        ? { status: 'pending', tag: result.tag }
        : { status: 'ready', tag: result.tag },
    )
  }

  return (
    <form className="payees__claim" onSubmit={(e) => void claim(e)}>
      <label className="payees__labelText" htmlFor={`${ids}-claim`}>
        Choose your tag
      </label>
      <div className="payees__tagInputRow">
        <span className="payees__at" aria-hidden="true">
          @
        </span>
        <input
          id={`${ids}-claim`}
          aria-describedby={`${ids}-claim-hint`}
          className="payees__input"
          value={typed}
          maxLength={30}
          placeholder="mia"
          onChange={(e) => setTyped(e.target.value)}
        />
        <button
          type="submit"
          className="payees__submit"
          // Disabled only when we KNOW it is taken. A check that failed leaves
          // the button live: refusing to let somebody try because we could not
          // look is worse than letting the claim answer for itself.
          disabled={busy || bareTag(typed) === '' || free.status === 'taken'}
        >
          {busy ? 'Claiming…' : 'Claim it'}
        </button>
      </div>
      <span id={`${ids}-claim-hint`} className="payees__hint">
        {/*
          The character set is the BACKEND's and is not a style choice: a tag has
          to survive a URL and an ISO-20022 payment reference, and those two
          together leave letters, digits and single hyphens.
        */}
        3 to 30 characters — letters, digits and single hyphens. This is the name people pay you
        at, so it is worth getting right: changing it later has a cooldown.
      </span>

      {/*
        WHETHER IT IS FREE, before the claim rather than after it. "Not available"
        discovered on submit is a form somebody fills in twice; and a name that
        would be HELD for review is worth knowing about first, which is the
        contract's own reason for publishing that flag separately — "so the
        customer chooses knowingly rather than discovering that their handle does
        not work".
      */}
      {free.status === 'free' ? (
        <p className="payees__free">@{free.tag} is free.</p>
      ) : free.status === 'needs_review' ? (
        <p className="payees__hint">
          @{free.tag} is free, but we would check it before it goes live — names close to a
          well-known one get a look first. It would be yours the whole time.
        </p>
      ) : free.status === 'taken' ? (
        <p className="payees__error">{unavailableBecause(free.reason)}</p>
      ) : free.status === 'unavailable' ? (
        // NOT "free". Saying so would invite a claim we have no basis to expect
        // to succeed.
        <p className="payees__hint">We could not check that one just now.</p>
      ) : null}

      {error !== undefined ? <p className="payees__error">{error}</p> : null}
    </form>
  )
}

/**
 * Find somebody on Tesora by their tag, and keep them (go-wallet#1066).
 *
 * # This is a LOOKUP, not a search
 *
 * There is no endpoint that lists tags and there is deliberately not going to
 * be: a directory of who banks here is the thing a public payment name must not
 * become. You type a tag you were given and it is confirmed, exactly as typing
 * an account number is. This screen must not build a search out of repeated
 * lookups either.
 *
 * # What comes back is a MASK
 *
 * "M••• P•••", never the name. A sender who was given the tag by Maria confirms
 * it; a sender who was given it by nobody learns two initials. The confirmation
 * is the whole point — you should see who you are about to keep before you keep
 * them.
 *
 * # Four outcomes and they are not one outcome
 *
 *   found        somebody we can pay answers to that tag
 *   no_match     nobody we can pay does — ONE answer for "no such tag", "that
 *                account is closed" and "that account cannot be credited",
 *                because telling them apart reports a stranger's compliance
 *                status to anybody who typed their public name
 *   malformed    the shape is wrong, which is something you can fix
 *   unavailable  we could NOT LOOK, which is not the same as nobody
 */
function FindByTag() {
  const api = useApi()
  const ids = useId()
  const [typed, setTyped] = useState('')
  const [state, setState] = useState<TagLookupState>({ status: 'idle' })
  const [label, setLabel] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string>()
  const [saved, setSaved] = useState<string>()
  const [mine, setMine] = useState<MyTagState>({ status: 'loading' })

  useEffect(() => {
    let live = true
    void fetchMyTag(api).then((m) => {
      if (live) setMine(m)
    })
    return () => {
      live = false
    }
  }, [api])

  const find = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaved(undefined)
    setSaveError(undefined)
    setState({ status: 'looking' })
    const result = await lookupTag(api, typed)
    setState(result)
    // Pre-fill the label with the tag, which is the only name we have — the
    // display name is a MASK and would put "M••• P•••" in somebody's list.
    if (result.status === 'found') setLabel(bareTag(typed))
  }

  const keep = async () => {
    if (state.status !== 'found') return
    setSaving(true)
    setSaveError(undefined)
    const failed = await saveByTag(api, state.tag, label)
    setSaving(false)
    if (failed !== undefined) {
      setSaveError(failed)
      return
    }
    setSaved(label)
    setState({ status: 'idle' })
    setTyped('')
  }

  return (
    <section className="payees__tag" aria-label="Find someone by tag">
      <h2 className="payees__addTitle">Find someone on Tesora</h2>

      {mine.status === 'ready' ? (
        <p className="payees__hint">
          Yours is <strong className="payees__mine">@{mine.tag}</strong> — give it to anybody who
          needs to pay you.
        </p>
      ) : mine.status === 'pending' ? (
        // HELD, NOT LIVE. Nobody else can take it and it does not resolve yet.
        // Showing it as working would put a live-looking payment address in
        // front of somebody whose payers would reach nobody.
        <p className="payees__hint">
          <strong className="payees__mine">@{mine.tag}</strong> is yours and is being checked.
          Nobody else can take it, and it will not reach you until that is done.
        </p>
      ) : mine.status === 'loading' ? (
        <p className="payees__hint">Reading your tag…</p>
      ) : mine.status === 'unavailable' ? (
        // NOT "you have no tag". We could not look, and saying otherwise sends
        // somebody to claim a name they may already hold.
        <p className="payees__hint">We could not read your own tag just now.</p>
      ) : (
        <ClaimTag onClaimed={(m) => setMine(m)} />
      )}

      <form className="payees__tagRow" onSubmit={(e) => void find(e)}>
        <label className="payees__labelText" htmlFor={`${ids}-tag`}>
          Their tag
        </label>
        <div className="payees__tagInputRow">
          <input
            id={`${ids}-tag`}
            aria-describedby={`${ids}-tag-hint`}
            className="payees__input"
            value={typed}
            placeholder="@mia"
            onChange={(e) => {
              setTyped(e.target.value)
              setState({ status: 'idle' })
            }}
          />
          <button type="submit" className="payees__submit" disabled={bareTag(typed) === ''}>
            <Icon name="Search" size={18} />
            <span>{state.status === 'looking' ? 'Looking…' : 'Look up'}</span>
          </button>
        </div>
        <span id={`${ids}-tag-hint`} className="payees__hint">
          You have to know the tag — there is no directory of who banks here, deliberately.
        </span>
      </form>

      {state.status === 'found' ? (
        <div className="payees__match">
          <p className="payees__matchLine">
            {/* THE MASK, exactly as the backend returns it. Never a real name. */}
            <strong>@{state.tag}</strong> is <strong>{state.match.displayName}</strong>, holding{' '}
            {state.match.currency}.
          </p>
          {state.match.currentTag !== undefined ? (
            <p className="payees__hint">
              They go by <strong>@{state.match.currentTag}</strong> now. @{state.tag} still reaches
              them, but the newer one is the one to keep.
            </p>
          ) : null}
          <div className="payees__tagInputRow">
            <input
              className="payees__input"
              // A DIFFERENT label from the add form's below, which asks the
              // same question about a different person. Two controls with one
              // accessible name are two controls a screen reader cannot tell
              // apart, and the tests could not address either.
              aria-label="What to call them"
              value={label}
              maxLength={60}
              onChange={(e) => setLabel(e.target.value)}
            />
            <button
              type="button"
              className="payees__submit"
              disabled={saving || label.trim() === ''}
              onClick={() => void keep()}
            >
              {saving ? 'Keeping…' : 'Keep them'}
            </button>
          </div>
          {saveError !== undefined ? <p className="payees__error">{saveError}</p> : null}
        </div>
      ) : state.status === 'no_match' ? (
        // ONE sentence for three causes. See the component header.
        <p className="payees__error">
          We cannot pay that tag. Check the spelling — and note that a tag whose owner has closed
          their account reads the same way.
        </p>
      ) : state.status === 'malformed' ? (
        <p className="payees__error">{state.reason}</p>
      ) : state.status === 'unavailable' ? (
        // NOT "no such person". Saying that when we could not look is a false
        // statement about somebody else.
        <p className="payees__error">We could not look that up. {state.reason}</p>
      ) : null}

      {saved !== undefined ? <p className="payees__saved">Kept {saved}.</p> : null}
    </section>
  )
}

/** The add form. */
function AddPayee({ onSaved }: { onSaved: () => Promise<void> }) {
  const api = useApi()
  const [label, setLabel] = useState('')
  const [creditorName, setCreditorName] = useState('')
  const [creditorIBAN, setCreditorIBAN] = useState('')
  const [creditorBIC, setCreditorBIC] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const [saved, setSaved] = useState<string>()
  // Explicit ids rather than a wrapping <label>. A label that wraps both the
  // input and its hint gives the field an accessible name of the two
  // concatenated — "Your name for themOnly you see this." — which is what a
  // screen reader announces and what a test cannot address. The hint is bound
  // with aria-describedby instead, which is where a hint belongs.
  const ids = useId()

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(undefined)
    setSaved(undefined)
    const result = await saveBeneficiary(api, {
      label,
      creditorName,
      creditorIBAN,
      // Trimmed to nothing means ABSENT, not empty. SEPA needs no BIC and the
      // contract omits the field rather than sending one of nothing.
      ...(creditorBIC.trim() !== '' ? { creditorBIC: creditorBIC.trim() } : {}),
    })
    setBusy(false)
    if (typeof result === 'string') {
      setError(result)
      return
    }
    setLabel('')
    setCreditorName('')
    setCreditorIBAN('')
    setCreditorBIC('')
    // Named, so saving the same account twice reads as the update it is rather
    // than looking like nothing happened.
    setSaved(`${result.label} — ${result.masked}`)
    await onSaved()
  }

  return (
    <form className="payees__add" onSubmit={(e) => void submit(e)}>
      <h2 className="payees__addTitle">Add someone</h2>

      <div className="payees__fields">
        <div className="payees__field">
          <label className="payees__labelText" htmlFor={`${ids}-label`}>
            Your name for them
          </label>
          <input
            id={`${ids}-label`}
            aria-describedby={`${ids}-label-hint`}
            className="payees__input"
            value={label}
            maxLength={60}
            placeholder="Acme Studios"
            onChange={(e) => setLabel(e.target.value)}
            required
          />
          <span id={`${ids}-label-hint`} className="payees__hint">
            Only you see this.
          </span>
        </div>

        <div className="payees__field">
          <label className="payees__labelText" htmlFor={`${ids}-name`}>
            Name on their account
          </label>
          <input
            id={`${ids}-name`}
            aria-describedby={`${ids}-name-hint`}
            className="payees__input"
            value={creditorName}
            maxLength={140}
            placeholder="Acme Studios GmbH"
            onChange={(e) => setCreditorName(e.target.value)}
            required
          />
          <span id={`${ids}-name-hint`} className="payees__hint">
            Exactly as their bank holds it. A mismatch can have the payment returned.
          </span>
        </div>

        <div className="payees__field payees__field--wide">
          <label className="payees__labelText" htmlFor={`${ids}-iban`}>
            Account number (IBAN)
          </label>
          <input
            id={`${ids}-iban`}
            aria-describedby={`${ids}-iban-hint`}
            className="payees__input tnum"
            value={creditorIBAN}
            placeholder="DE89 3704 0044 0532 0130 00"
            onChange={(e) => setCreditorIBAN(e.target.value)}
            required
          />
          <span id={`${ids}-iban-hint`} className="payees__hint">
            Spacing and capitals do not matter. It is checked before it is saved, so a mistyped
            digit is caught here rather than by their bank days later.
          </span>
        </div>

        <div className="payees__field">
          <label className="payees__labelText" htmlFor={`${ids}-bic`}>
            BIC (optional)
          </label>
          <input
            id={`${ids}-bic`}
            aria-describedby={`${ids}-bic-hint`}
            className="payees__input tnum"
            value={creditorBIC}
            maxLength={11}
            placeholder="DEUTDEFF"
            onChange={(e) => setCreditorBIC(e.target.value)}
          />
          <span id={`${ids}-bic-hint`} className="payees__hint">
            Leave it out for a payment inside Europe.
          </span>
        </div>
      </div>

      <div className="payees__submitRow">
        <button type="submit" className="payees__submit" disabled={busy}>
          <Icon name="Recipients" size={18} />
          <span>{busy ? 'Saving…' : 'Save payee'}</span>
        </button>
        {saved !== undefined ? <span className="payees__saved">Saved {saved}</span> : null}
        {error !== undefined ? <span className="payees__error">{error}</span> : null}
      </div>

      {/*
        Said on the form rather than discovered on a second save. The backend
        keys an entry on the account number, so saving one already saved updates
        it — which is the behaviour somebody wants and the opposite of what a
        list with two rows for one account would give them.
      */}
      <p className="payees__note">
        Saving an account you already have updates that entry instead of adding a second one. You
        can rename a payee any time; to change where the money goes, add the new account and remove
        the old one.
      </p>
    </form>
  )
}
